export enum ClientStatus {
  Onboarding = 'Onboarding',
  Active = 'Active',
  PendingPayment = 'Pending Payment',
  NeedsAttention = 'Needs Attention',
  Churned = 'Churned',
}

export enum SatisfactionLevel {
  VerySatisfied = 'Very Satisfied',
  Satisfied = 'Satisfied',
  Neutral = 'Neutral',
  Dissatisfied = 'Dissatisfied',
  VeryDissatisfied = 'Very Dissatisfied',
}

export enum RenewalPrediction {
  LikelyToRenew = 'Likely to Renew',
  Uncertain = 'Uncertain',
  UnlikelyToRenew = 'Unlikely to Renew',
}

export enum UserRole {
  SalesAgent = 'Sales Agent',
  Operator = 'Operator',
  Manager = 'Manager',
  QualityAssurance = 'Quality Assurance',
  Admin = 'Admin',
  WebManager = 'Web Manager',
  Superadmin = 'Superadmin',
}

export enum UserStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Suspended = 'Suspended',
}

export enum TaskStatus {
  Pending = 'Pending',
  Reported = 'Reported as Done',
  Approved = 'Approved',
  Rescheduled = 'Rescheduled',
}

export type RemarkAction = 'Approved' | 'Declined' | 'Reported' | 'Note' | 'Rescheduled';

export interface Remark {
  id: string;
  text: string;
  authorRole: string;
  timestamp: string;
  action: RemarkAction;
}

export interface OnboardingTask {
  _id?: string;
  type?: string;
  status: TaskStatus;
  remarks: Remark[];
  scheduledDate: string | null;
  scheduledBy?: string | null;
  history?: { scheduledDate: string | null; status: TaskStatus }[];
}

export interface OnboardingTasks {
  hasWebsite: boolean;
  websiteDelivered: OnboardingTask;
  hasSalesFunnel: boolean;
  salesFunnelDelivered: OnboardingTask;
  hasAIWhatsAppBot: boolean;
  aiWhatsAppBotUsers?: number;
  aiWhatsAppBotDelivered: OnboardingTask;
  adminTrainingCompleted: OnboardingTask;
  agentTrainingCompleted: OnboardingTask;
  websiteTrainingCompleted: OnboardingTask;
  websiteType?: string;
}

export interface FileAttachment {
  id: string;
  name: string;
  type: string;
  uploadDate: string;
  url?: string;
  sourceType?: 'upload' | 'contract';
  contractId?: string;
  mimeType?: string;
  isContractDocument?: boolean;
  signatureStatus?: 'pending' | 'signed' | 'unsigned';
  signedAt?: string;
  signatureNotes?: string;
  linkedInvoiceId?: string;
}

export type BillingPeriodPaymentType = 'Yearly' | 'Monthly' | 'OneTime' | 'Installment';
export type BillingPeriodStatus = 'upcoming' | 'active' | 'closed' | 'void';
export type BillingPeriodSource =
  | 'signup'
  | 'renewal'
  | 'conversion'
  | 'migration'
  | 'payment_adjust';
export type BillingPeriodRenewalStatus =
  | 'pending'
  | 'partial'
  | 'paid'
  | 'overdue'
  | 'upcoming'
  | 'void';

export interface BillingPeriod {
  id: string;
  index: number;
  paymentType: BillingPeriodPaymentType;
  startDate: string;
  endDate?: string | null;
  /** Base deal amount (excl. VAT & Stripe). */
  expectedAmount: number;
  originalExpectedAmount: number;
  /** Expected VAT on this period (editable). */
  vatAmount?: number;
  /** Expected Stripe surcharge on this period (editable). */
  stripeFeeAmount?: number;
  adjustmentNote?: string;
  status: BillingPeriodStatus;
  createdAt?: string;
  updatedAt?: string;
  source?: BillingPeriodSource;
}

export interface Client {
  id: string;
  name: string;
  company: string;
  /** Accounting legal entity code (Company.code) — determines which books invoices post to */
  companyId?: string;
  email: string;
  phone?: string;
  address?: string;
  vatNumber?: string;
  numberOfUsers: number;
  status: ClientStatus;
  onboardingTasks: OnboardingTasks;
  qualityCheckRemarks: string;
  clientFeedback: string;
  satisfactionLevel: SatisfactionLevel | null;
  renewalPrediction: RenewalPrediction | null;
  createdAt: string;
  modifiedAt: string;
  salesAgentId?: string;
  totalPrice?: number;
  isPaymentPending?: boolean;
  pendingAmount?: number;
  paymentDueDate?: string | null;
  attachments: FileAttachment[];
  signupDate?: string;
  leadSource?: string;
  helpingSalesAgent?: string;
  paymentType?: 'Yearly' | 'Monthly';
  firstPayment?: number;
  monthlyPayment?: number;
  yearlyBillingStartDate?: string | null;
  monthlyBillingStartDate?: string | null;
  billingConvertedAt?: string | null;
  splitPayments?: boolean;
  // Stripe Integration Fields
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionStatus?: string;
  subscriptionPeriodEnd?: string;
  // Agent/Company type
  type?: 'company' | 'agent';
  // Onboarding status for agents
  onboardingStatus?: 'not_started' | 'in_progress' | 'pending' | 'completed';
  // Owner/Helper commission split percentage
  ownerSplitPercent?: 50 | 75;
  /** Amount splits for future payments. Empty keeps the owner/helper split. */
  commissionAssignments?: CommissionAssignment[];
  tags?: {
    tagId: string;
    name: string;
    color: string;
    icon: string;
    category: 'sales' | 'renewal' | 'support';
    assignedByName?: string;
    assignedAt?: string;
  }[];
  /** Parallel CRM billing periods ledger (Financials tab). Does not replace legacy deal fields. */
  billingPeriods?: BillingPeriod[];
  /** Denormalized package/addon snapshot from create/edit wizard (not linked to subscriptions). */
  wizardSnapshot?: ClientWizardSnapshot | null;
  /** Field-level history from the client wizard. */
  wizardChangeLog?: ClientWizardChangeLogEntry[];
}

export interface CommissionAssignment {
  userId?: string;
  username: string;
  name?: string;
  role: 'sales' | 'support' | 'retention';
  paymentType: 'firstPayment' | 'renewal' | 'other';
  percent: number;
}

export interface CommissionShare {
  userId?: string;
  username: string;
  name?: string;
  role?: 'sales' | 'support' | 'retention';
  paymentType?: 'firstPayment' | 'renewal' | 'other';
  percent: number;
  amount: number;
}

export interface ClientWizardAddonSnapshot {
  addonId?: string;
  key?: string;
  name: string;
  slug?: string;
  quantity: number;
  unitPrice: number;
  billingType: string;
  isPackageIncluded?: boolean;
  isUserAddon?: boolean;
}

export interface ClientWizardSnapshot {
  sourceType?: 'proposal' | 'contract' | 'manual' | null;
  sourceProposalId?: string | null;
  sourceContractId?: string | null;
  billingPeriod?: 'yearly' | 'monthly';
  totalPrice?: number;
  package?: {
    packageId?: string | null;
    name?: string;
    slug?: string;
    includedUsers?: number;
    monthlyPrice?: number;
    yearlyPrice?: number;
  } | null;
  addons?: ClientWizardAddonSnapshot[];
  computedNumberOfUsers?: number;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface ClientWizardChangeLogEntry {
  at: string;
  by?: string;
  byName?: string;
  changes: { field: string; from: unknown; to: unknown }[];
}

export interface ActivityLog {
  id: string;
  description: string;
  timestamp: string;
  authorRole: string;
  clientId: string;
}

export interface Role {
  _id: string;
  name: string;
  description: string;
}

export interface User {
  _id: string;
  id?: string;
  name: string;
  email: string;
  phone: string;
  roles: Role[] | string[];
  status: UserStatus;
  username: string;
  commissionSlabId?: string | null;
  renewalCommissionSlabId?: string | null;
  otherCommissionSlabId?: string | null;
  isSalesAgent?: boolean;
  isSupportAgent?: boolean;
  /** REM Voice agent ObjectId for in-app dialer */
  remVoiceAgentId?: string | null;
  remVoiceDialerMode?: 'simple' | 'full' | 'both' | 'crmOnlyDial';
  remVoiceReceiveEnabled?: boolean;
  workingOffice?: string;
  passkeyCount?: number;
  hasPasskey?: boolean;
  passkeyRemindersDisabled?: boolean;
}

export interface Transaction {
  id: string;
  _id?: string;
  clientId: string;
  amount: number;
  vat_amount: number;
  transaction_amount?: number;
  transactionDate: string;
  details?: string;
  paymentMethod: 'stripe' | 'bank' | 'cash';
  bankName?: string; // For Bank payments: 'Mashreq Bank' or 'WIO Bank'
  transactionType?: 'firstPayment' | 'renewal' | 'other'; // 'firstPayment', 'renewal', or 'other', defaults to 'firstPayment'
  /** Frozen amount split. When true, commission uses commissionShares instead of owner/helper. */
  commissionSharesCaptured?: boolean;
  commissionShares?: CommissionShare[];
  paymentLink?: string;
  receiptUrl?: string;
  receiptFileName?: string;
  createdBy: string;
  createdAt: string;
  // Accounting integration fields
  accountingPosted?: boolean;
  accountingJournalEntryId?: string;
  stripeProcessingFee?: number; // Customer pass-through surcharge (e.g. 2.5%)
  stripeFeeActual?: number | null; // Actual Stripe processing fee from balance_transaction
  stripeNetAmount?: number | null; // Net received after Stripe fees
  /** Set by API enrichment — fee is confirmed distinct from customer surcharge */
  stripeFeeVerified?: boolean;
  /** UAE formula estimate when actual fee is not verified */
  stripeFeeEstimated?: number | null;
  accountingPostedAt?: string;
  accountingPostedBy?: string;
  // Link to Stripe transaction (1-1 connection)
  stripeTransactionId?: string;
  /** Optional link to Client.billingPeriods[].id */
  billingPeriodId?: string | null;
}

export interface CommentCategory {
  id: string;
  _id?: string;
  name: string;
  color: string;
  description?: string;
  isActive: boolean;
  order: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ClientComment {
  id: string;
  _id?: string;
  clientId: string;
  content: string;
  authorId: string;
  authorName: string;
  authorRole: string;
  categories: CommentCategory[];
  retentionMonth?: number;
  retentionYear?: number;
  parentCommentId?: string;
  isEdited: boolean;
  editedAt?: string;
  isDeleted: boolean;
  deletedAt?: string;
  deletedBy?: string;
  createdAt: string;
  updatedAt: string;
  replies?: ClientComment[];
}

// Tickets Module Types
export enum TicketStatus {
  New = 'new',
  Triaged = 'triaged',
  InProgress = 'in_progress',
  WaitingClient = 'waiting_client',
  Resolved = 'resolved',
  Closed = 'closed'
}

export enum TicketPriority {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  Urgent = 'urgent'
}

export enum TicketType {
  Support = 'support',
  Feature = 'feature',
  Complaint = 'complaint',
  Billing = 'billing',
  Website = 'website',
  Project = 'project'
}

export interface TicketAttachment {
  s3Key: string;
  fileName: string;
  size: number;
  mimeType: string;
  uploadedAt: string;
}

export interface TicketComment {
  _id: string;
  ticketId: string;
  authorId: User;
  body: string;
  isInternal: boolean;
  attachments: TicketAttachment[];
  createdAt: string;
  updatedAt: string;
  // Status tracking
  ticketStatusAtTime: TicketStatus;
  statusChange?: {
    fromStatus: TicketStatus;
    toStatus: TicketStatus;
  };
}

export interface Ticket {
  _id: string;
  ticketNumber?: string;
  clientId: Client;
  createdBy: User;
  assignedTo?: User | null;
  watchers: User[];
  type: TicketType;
  status: TicketStatus;
  priority: TicketPriority;
  source: 'portal' | 'email' | 'whatsapp' | 'internal';
  subject: string;
  description: string;
  tags: string[];
  slaMetrics?: {
    firstResponseDue?: Date | null;
    resolutionDue?: Date | null;
    firstResponseAt?: Date | null;
    resolvedAt?: Date | null;
    isFirstResponseBreached?: boolean;
    isResolutionBreached?: boolean;
    escalationLevel?: number;
  };
  related: {
    websiteId?: string | null;
    projectId?: string | null;
    invoiceId?: string | null;
    whatsappConversationId?: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TicketStats {
  total: number;
  new: number;
  inProgress: number;
  waitingClient: number;
  overdue: number;
  resolvedToday: number;
  createdToday?: number;
  totalPending?: number;
  slaBreached: number;
  avgResponseTime: number;
  slaBreachRate: number;
}

export interface TicketFilteredCounts {
  summary?: {
    total: number;
    totalPending: number;
    overdue: number;
    createdToday: number;
    resolvedToday: number;
    slaBreached: number;
  };
  excludedTypeCounts?: Record<string, number>;
  types: Record<string, number>;
  priorities: Record<string, number>;
  statuses: Record<string, number>;
}

// Client Onboarding Tasks Types
export enum ClientOnboardingTaskStatus {
  Pending = 'Pending',
  NotNeeded = 'Not Needed',
  Started = 'Started',
  WaitingClient = 'Waiting Client',
  WaitingREMSupport = 'Waiting REM Support',
  OnHold = 'On Hold',
  Completed = 'Completed'
}

export interface ClientOnboardingTaskStatusHistory {
  status: string;
  changedAt: string;
  changedBy?: string;
  changedByName?: string;
  notes?: string;
}

export interface OnboardingPersonRef {
  _id: string;
  name: string;
  username?: string;
  companies?: { companyId: string; companyName: string }[];
}

export interface OnboardingDepartmentRef {
  _id: string;
  name: string;
}

export interface ClientOnboardingTask {
  _id: string;
  id?: string;
  clientId: string;
  taskTemplateId?: string | null;
  taskName: string;
  status: ClientOnboardingTaskStatus;
  order: number;
  notes?: string;
  statusHistory: ClientOnboardingTaskStatusHistory[];
  assignedTo?: {
    _id: string;
    name: string;
    username: string;
  } | null;
  department?: OnboardingDepartmentRef | null;
  responsiblePeople?: OnboardingPersonRef[];
  defaultResponsible?: OnboardingPersonRef | null;
  dueDate?: string | null;
  completedAt?: string | null;
  completedBy?: {
    _id: string;
    name: string;
    username: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  // Email tracking fields
  startedEmailSentAt?: string | null;
  completedEmailSentAt?: string | null;
  waitingEmailSentAt?: string | null;
}

export interface OnboardingEmailTemplateContent {
  subject: string;
  heading: string;
  body: string;
  customized: boolean;
}

export interface OnboardingTaskTemplate {
  _id: string;
  id?: string;
  name: string;
  description?: string;
  order: number;
  isActive: boolean;
  isDefault: boolean;
  createdBy?: {
    _id: string;
    name: string;
    username: string;
  };
  departmentId?: OnboardingDepartmentRef | string | null;
  responsibleUserIds?: (OnboardingPersonRef | string)[];
  defaultResponsibleUserId?: OnboardingPersonRef | string | null;
  emailTemplates?: {
    started?: OnboardingEmailTemplateContent;
    completed?: OnboardingEmailTemplateContent;
    waiting?: OnboardingEmailTemplateContent;
  };
  createdAt: string;
  updatedAt: string;
}

// Review System Types
export enum ReviewSection {
  ONBOARDING = 'onboarding',
  OFFPLAN_PROJECTS = 'offplan_projects',
  WEBSITE_DEVELOPMENT = 'website_development',
  WHATSAPP_SALES_FUNNEL = 'whatsapp_sales_funnel',
  LEAD_MANAGEMENT = 'lead_management',
  REAL_ESTATE_MANAGEMENT = 'real_estate_management',
  PROPERTY_PORTALS = 'property_portals',
  META_INTEGRATION = 'meta_integration',
  LEAD_SOURCES = 'lead_sources',
}

export const ReviewSectionLabels: Record<ReviewSection, string> = {
  [ReviewSection.ONBOARDING]: 'Onboarding Experience',
  [ReviewSection.OFFPLAN_PROJECTS]: 'Off-plan Projects',
  [ReviewSection.WEBSITE_DEVELOPMENT]: 'Website Development',
  [ReviewSection.WHATSAPP_SALES_FUNNEL]: 'WhatsApp Sales Funnel',
  [ReviewSection.LEAD_MANAGEMENT]: 'Lead Management',
  [ReviewSection.REAL_ESTATE_MANAGEMENT]: 'Real Estate Management',
  [ReviewSection.PROPERTY_PORTALS]: 'Property Portal Integrations',
  [ReviewSection.META_INTEGRATION]: 'Meta (Facebook/Instagram) Integration',
  [ReviewSection.LEAD_SOURCES]: 'Lead Sources Integration',
};

export interface ClientReviewLink {
  _id: string;
  clientId: string;
  token: string;
  title: string;
  sections: ReviewSection[];
  customMessage?: string;
  createdBy?: {
    _id: string;
    name: string;
    username: string;
  };
  status: 'active' | 'submitted' | 'expired' | 'cancelled';
  expiresAt?: string;
  submittedAt?: string;
  url: string;
  review?: {
    _id: string;
    overallHappinessScore: number;
    npsScore: number;
    npsCategory: 'promoter' | 'passive' | 'detractor';
    submittedAt: string;
    reviewerName: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ReviewRating {
  score: number;
  comment?: string;
}

export interface SectionReview {
  section: string;
  enabled: boolean;
  used: boolean | null;
  notUsedReason?: string;
  overallRating?: number;
  ratings?: {
    easeOfUse?: ReviewRating;
    functionality?: ReviewRating;
    support?: ReviewRating;
    valueForMoney?: ReviewRating;
  };
  liked?: string;
  improvements?: string;
  additionalComments?: string;
}

export interface OnboardingReview {
  overallExperience?: number;
  communicationRating?: number;
  timelinessRating?: number;
  clarityRating?: number;
  supportRating?: number;
  whatWentWell?: string;
  whatCouldImprove?: string;
  wouldRecommend?: boolean;
  recommendScore?: number;
}

export interface ClientReview {
  _id: string;
  reviewLinkId: string | ClientReviewLink;
  clientId: string;
  reviewerName: string;
  reviewerEmail?: string;
  reviewerRole?: string;
  onboarding?: OnboardingReview;
  sectionReviews: SectionReview[];
  overallSatisfaction: number;
  npsScore?: number;
  overallFeedback?: string;
  suggestions?: string;
  wouldContinue?: boolean;
  testimonial?: string;
  allowTestimonialPublic?: boolean;
  calculatedScores?: {
    overallHappinessScore: number;
    onboardingScore?: number;
    featureScores?: { section: string; score: number }[];
    npsCategory?: 'promoter' | 'passive' | 'detractor';
  };
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
}

// Demo Request Types
export enum DemoRequestStatus {
  Pending = 'pending',
  DemoCreated = 'demo_created',
  DemoActive = 'demo_active',
  Converted = 'converted',
  Expired = 'expired',
  Rejected = 'rejected'
}

export interface DemoCredentials {
  username: string | null;
  password: string | null;
  loginUrl: string | null;
}

export interface DemoAppLinks {
  ios: string;
  android: string;
  web: string;
}

export interface DemoStatusHistory {
  status: string;
  changedAt: string;
  changedBy?: {
    _id: string;
    name: string;
    username: string;
  };
  notes?: string;
}

export interface DemoFollowUp {
  note: string;
  scheduledDate?: string;
  completedAt?: string;
  createdBy?: {
    _id: string;
    name: string;
    username: string;
  };
  createdAt: string;
}

export interface DemoReminderSent {
  sentAt: string;
  daysBeforeExpiry?: number;
  type: 'expiry_reminder' | 'credentials' | 'payment_reminder';
}

export interface DemoRequest {
  _id: string;
  id: string;

  // Company Information
  companyName: string;
  companyLogo?: string;
  companyLicense?: string;
  companyLicenseFile?: string;

  // Contact Information
  contactNumber: string;
  email: string;

  // Owner/Admin Details
  ownerName: string;
  ownerPicture?: string;
  ownerDesignation?: string;

  // Demo Configuration
  demoPeriod: number;
  demoStartDate?: string;
  demoEndDate?: string;

  // Permissions/Features
  permissionsDescription?: string;

  // Demo Credentials
  demoCredentials: DemoCredentials;

  // Status
  status: DemoRequestStatus;
  statusHistory: DemoStatusHistory[];

  // Payment Information
  paymentLink?: string;
  bankDetails?: string;

  // Reminder Settings
  reminderEnabled: boolean;
  reminderDays: number[];
  remindersSent: DemoReminderSent[];

  // Notes
  notes?: string;
  internalNotes?: string;

  // Follow-ups
  followUps: DemoFollowUp[];

  // App Links
  appLinks: DemoAppLinks;

  // Conversion tracking
  convertedClientId?: {
    _id: string;
    name: string;
    company: string;
  };
  conversionDate?: string;

  // Rejection tracking
  rejectionReason?: string;
  rejectedAt?: string;
  rejectedBy?: {
    _id: string;
    name: string;
    username: string;
  };

  // Created/Assigned by
  createdBy: {
    _id: string;
    name: string;
    username: string;
    email: string;
  };
  assignedTo?: {
    _id: string;
    name: string;
    username: string;
    email: string;
  };

  // Email tracking
  credentialEmailSentAt?: string;
  paymentEmailSentAt?: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;

  // Virtual fields
  remainingDays?: number;
  demoProgress?: number;
}

export interface DemoRequestStats {
  total: number;
  pending: number;
  demoCreated: number;
  demoActive: number;
  converted: number;
  expired: number;
  rejected: number;
  expiringSoon: number;
}
